import axios from "axios";
import { GROUPS_ENDPOINTS } from "./groupsConstants";

/**
 * Fetches the 10 most recently created groups (admin dashboard).
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional signal to cancel the request
 * @returns {Promise<Array>} Array of serialized groups
 */
export async function fetchLastGroups({ signal } = {}) {
    const { data } = await axios.get(GROUPS_ENDPOINTS.last, { signal });
    return data;
}

/**
 * Searches groups by substring (matches name, description, member email/surname, linked user fields).
 * @param {string} substring - Search term (case-insensitive server-side)
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array>} Up to 10 matching groups
 */
export async function searchGroups(substring, { signal } = {}) {
    const { data } = await axios.get(GROUPS_ENDPOINTS.search(substring), { signal });
    return data;
}

/**
 * Fetches the full detail of a group (members, wallets, transactions, services, banking).
 * @param {number|string} groupId
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Object>} Serialized group
 */
export async function fetchGroupDetail(groupId, { signal } = {}) {
    const { data } = await axios.get(GROUPS_ENDPOINTS.detail(groupId), { signal });
    return data;
}

/**
 * Updates a group profile (name, description, photos).
 * Sends multipart/form-data when a File is provided, JSON otherwise.
 * @param {number|string} groupId
 * @param {Object} payload - { name?, description?, photo?, cover_photo? }
 * @returns {Promise<Object>} Updated serialized group
 */
export async function updateGroup(groupId, payload) {
    const hasFile = payload?.photo instanceof File || payload?.cover_photo instanceof File;
    if (hasFile) {
        const form = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) form.append(key, value);
        });
        const { data } = await axios.put(GROUPS_ENDPOINTS.update(groupId), form);
        return data;
    }
    const { data } = await axios.put(GROUPS_ENDPOINTS.update(groupId), payload);
    return data;
}

/**
 * Deletes a group. Server returns "deleted" or rejects with explicit business errors.
 * @param {number|string} groupId
 * @returns {Promise<string>} "deleted"
 */
export async function deleteGroup(groupId) {
    const { data } = await axios.delete(GROUPS_ENDPOINTS.remove(groupId));
    return data;
}

/**
 * Adds a member to the group.
 * @param {number|string} groupId
 * @param {Object} payload - { surname, email, role?, payout_percent?, is_leader?, can_* permissions }
 * @returns {Promise<Object>} Updated serialized group
 */
export async function addGroupMember(groupId, payload) {
    const { data } = await axios.post(GROUPS_ENDPOINTS.addMember(groupId), payload);
    return data;
}

/**
 * Updates a group member (role, percentages, permissions, leadership).
 * @param {number|string} memberId
 * @param {Object} payload - Same shape as addGroupMember
 * @returns {Promise<Object>} Updated serialized group
 */
export async function updateGroupMember(memberId, payload) {
    const { data } = await axios.put(GROUPS_ENDPOINTS.updateMember(memberId), payload);
    return data;
}

/**
 * Removes a member from the group. Triggers an email to the linked user.
 * @param {number|string} memberId
 * @returns {Promise<string>} "Member removed"
 */
export async function removeGroupMember(memberId) {
    const { data } = await axios.delete(GROUPS_ENDPOINTS.removeMember(memberId));
    return data;
}

/**
 * Transfers leadership to the given member (demotes other leaders).
 * @param {number|string} groupId
 * @param {number|string} memberId
 * @returns {Promise<string>} "success"
 */
export async function transferLeadership(groupId, memberId) {
    const { data } = await axios.put(GROUPS_ENDPOINTS.transferLeadership(groupId, memberId));
    return data;
}

/**
 * Toggles the wallet freeze state. Optional reason is persisted on freeze.
 * @param {number|string} groupId
 * @param {Object} [payload] - { reason? }
 * @returns {Promise<Object>} Updated wallet
 */
export async function toggleWalletFreeze(groupId, payload = {}) {
    const { data } = await axios.put(GROUPS_ENDPOINTS.walletFreeze(groupId), payload);
    return data;
}

/**
 * Confiscates the wallet balance (super-admin only). Reason is mandatory.
 * @param {number|string} groupId
 * @param {Object} payload - { reason }
 * @returns {Promise<Object>} { wallet, transaction, confiscated_amount }
 */
export async function confiscateWallet(groupId, payload) {
    const { data } = await axios.post(GROUPS_ENDPOINTS.walletConfiscate(groupId), payload);
    return data;
}

/**
 * Triggers a Stripe payout from the group wallet to the group or one of its members.
 * @param {number|string} groupId
 * @param {Object} payload - { amount, currency, destination, member_id? }
 * @returns {Promise<Object>} { transaction, wallet, destination }
 */
export async function bankTransferWallet(groupId, payload) {
    const { data } = await axios.post(GROUPS_ENDPOINTS.walletBankTransfer(groupId), payload);
    return data;
}

/**
 * Redispatches a group transaction across the selected members.
 * @param {number|string} groupId
 * @param {number|string} transactionId
 * @param {Object} payload - { participating_members: number[] }
 * @returns {Promise<Object>} { last_transaction_group_updated, new_group_wallet_transaction }
 */
export async function dispatchTransaction(groupId, transactionId, payload) {
    const { data } = await axios.put(GROUPS_ENDPOINTS.dispatchTransaction(groupId, transactionId), payload);
    return data;
}
